import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ListeRolePage.scss";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";
import { useGetRolesByCompanyQuery } from "store/role/role.api";
import CreateRoleComponent from "../CreateRoleComponent/CreateRoleComponent";
import { PERMISSIONS } from "constants/permissions/permissions";

const RolesPage = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [openMenuId, setOpenMenuId] = useState(null);
  const { toggleCreateModal } = useContext(CreateModalContext);

  const { data: roles, isLoading } = useGetRolesByCompanyQuery(companyId, {
    skip: !companyId,
  });

  // Fonction pour convertir les codes de permissions en labels
  const getPermissionLabels = (codes) => {
    if (!codes || codes.length === 0) return "";
    let labels = [];
    Object.values(PERMISSIONS).forEach((category) => {
      category.forEach((p) => {
        if (codes.includes(p.code)) {
          labels.push(t(p.label));
        }
      });
    });
    return labels.join(", ");
  };

  const headers = [
    { key: "name", label: t("role.create_form.name") },
    { key: "permissions", label: t("role.create_form.permission") },
    { key: "createdAt", label: t("role.table.createdAt") },
  ];

  const renderCell = (key, role) => {
    if (key === "createdAt")
      return new Date(role.createdAt).toLocaleDateString();

    if (key === "permissions") {
      return getPermissionLabels(role.permissions);
    }

    return role[key];
  };

  const handleEditRoles = (role) => {
    setOpenMenuId(null);
    toggleCreateModal(
      <CreateRoleComponent
        onClose={() => toggleCreateModal(<></>)}
        role={role}
        isUpdate={true}
      />,
      t("role.form.title_edit")
    );
  };

  const actionMenu = (role) => {
    const actions = [];

    // Edit Role
    if (currentUser?.roleId?.permissions.includes("edit_role")) {
      actions.push({
        label: t("role.actions.edit"),
        onClick: () => handleEditRoles(role),
      });
    }

    // Delete Role

    return actions.length > 0 ? (
      <CustomActionMenu
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        data={role}
        actions={actions}
      />
    ) : null;
  };

  // Vérification : est-ce que l’utilisateur a au moins une permission d’action sur les rôles ?
  const hasActionPermission = ["edit_role"].some((perm) =>
    currentUser?.roleId?.permissions.includes(perm)
  );

  const [searchText, setSearchText] = useState("");
  const onSearchRole = (text) => setSearchText(text.toLowerCase());

  const filteredRoles = useMemo(() => {
    if (!roles?.data) return [];
    if (searchText.length <= 1) return roles.data;

    return roles.data.filter((role) =>
      role.name?.toLowerCase().includes(searchText)
    );
  }, [roles?.data, searchText]);

  if (isLoading) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("role.header_title")}
        dataLength={filteredRoles.length}
        onSearch={onSearchRole}
      />
      <CustomTable
        headers={headers}
        data={filteredRoles}
        rowKey="_id"
        renderCell={renderCell}
        // on affiche la colonne action uniquement si l'utilisateur a des permissions
        {...(hasActionPermission && { actionMenu })}
      />
    </div>
  );
};

export default RolesPage;
